import type { PublicEvent } from "@homesite/api-contract";
import { queryKeys } from "@homesite/queries";
import { useQuery } from "@tanstack/react-query";
import { useApiClient } from "../lib/ApiClientContext";

export interface BatchEventsData {
	events: PublicEvent[];
	total: number;
	page: number;
	limit: number;
	totalPages: number;
	loading: boolean;
	error: Error | null;
}

/**
 * Fetches the paginated, anonymized event timeline for a single merkle batch.
 * Uses the GET /public/batches/:root/events endpoint.
 */
export function useBatchEvents(
	root: string,
	page = 1,
	limit = 20,
): BatchEventsData {
	const client = useApiClient();

	const query = useQuery({
		queryKey: queryKeys.publicVerification.batches.events(root, page, limit),
		queryFn: async () => {
			const result = await client.publicVerification.getBatchEvents({
				params: { root },
				query: { page, limit },
			});
			if (result.status !== 200) {
				throw new Error("Failed to load batch events");
			}
			return result.body;
		},
		enabled: root.length > 0,
	});

	return {
		events: query.data?.items ?? [],
		total: query.data?.total ?? 0,
		page: query.data?.page ?? page,
		limit: query.data?.limit ?? limit,
		totalPages: query.data?.totalPages ?? 0,
		loading: query.isLoading,
		error: query.error,
	};
}
